import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppHeader from "./components/AppHeader";
import { apiPost } from "./api";

/** Question generation uses AWS Bedrock only (no provider picker in UI). */
const AI_PROVIDER = "bedrock";

const SummaryPage = () => {
  const [formData, setFormData] = useState({
    course: "N/A",
    courseDisplayName: "N/A",
    module: "N/A",
    numQuestions: "N/A",
    mcqSplit: "N/A",
    tfSplit: "N/A",
    easyPct: 34,
    mediumPct: 33,
    hardPct: 33,
  });

  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const savedData = localStorage.getItem("formData");
    if (savedData) {
      setFormData(JSON.parse(savedData));
    }
  }, []);

  const handleGenerateQuestions = () => {
    setShowModal(true);
  };

  const handleModalConfirm = async () => {
    const { course, module, numQuestions, mcqSplit, tfSplit, easyPct, mediumPct, hardPct } = formData;
    if (!course || !module || course === "N/A" || module === "N/A") {
      setShowModal(false);
      setError("Please go back and select a course and module first.");
      return;
    }
    if (!numQuestions || numQuestions === "N/A" || !mcqSplit || mcqSplit === "N/A" || !tfSplit || tfSplit === "N/A") {
      setShowModal(false);
      setError("Please go back and select number of questions and MCQ/TF percentages.");
      return;
    }
    const ePct = parseInt(easyPct, 10) || 0;
    const mPct = parseInt(mediumPct, 10) || 0;
    const hPct = parseInt(hardPct, 10) || 0;
    if (ePct + mPct + hPct !== 100) {
      setShowModal(false);
      setError(`Difficulty mix must total 100% (currently ${ePct + mPct + hPct}%). Please go back and fix.`);
      return;
    }

    setShowModal(false);
    setIsLoading(true);
    setError(null);

    try {
      const data = await apiPost("/api/generate-questions", {
        course,
        module,
        numQuestions,
        mcqPercentage: mcqSplit,
        tfPercentage: tfSplit,
        easyPercentage: ePct,
        mediumPercentage: mPct,
        hardPercentage: hPct,
        aiProvider: AI_PROVIDER,
        userId: formData.userId || undefined,
      });

      const questions = data.questions || [];
      if (questions.length === 0) {
        setError("No questions were generated. Please try again.");
      } else {
        localStorage.setItem('generatedQuestions', JSON.stringify(questions));
        localStorage.setItem('generatedQuestionsProvider', AI_PROVIDER);
        if (Array.isArray(data.warnings) && data.warnings.length) {
          localStorage.setItem('generationWarnings', JSON.stringify(data.warnings));
        } else {
          localStorage.removeItem('generationWarnings');
        }
        navigate('/generate-question');
      }
    } catch (err) {
      setError(err.message || "Error generating questions. Check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalCancel = () => {
    setShowModal(false);
  };

  return (
    <div className="min-h-screen w-full bg-secondary">
      {/* Header */}
      <AppHeader titleSize="text-5xl" />

      {/* Banner */}
      <div className="bg-accent text-center py-4">
        <h2 className="text-2xl font-bold text-primary underline">Summary</h2>
      </div>

      {/* Content */}
      <div className="p-12">
        <h2 className="text-xl font-bold">You have chosen the following options:</h2>
        <div className="flex flex-wrap gap-3 mt-8">
          <span className="px-4 py-1 bg-gray-300 rounded">
            {formData.courseDisplayName && formData.courseDisplayName !== "N/A"
              ? formData.courseDisplayName
              : formData.course}
          </span>
          <span className="px-4 py-1 bg-gray-300 rounded">{formData.module}</span>
          <span className="px-4 py-1 bg-gray-300 rounded">
            {formData.numQuestions} Questions will be generated
          </span>
          <span className="px-4 py-1 bg-gray-300 rounded">
            {formData.mcqSplit}% will be Multiple Choice Questions
          </span>
          <span className="px-4 py-1 bg-gray-300 rounded">
            {formData.tfSplit}% will be True/False
          </span>
          <span className="px-4 py-1 bg-green-200 rounded">
            Easy: {formData.easyPct}% (Gemini)
          </span>
          <span className="px-4 py-1 bg-yellow-200 rounded">
            Medium: {formData.mediumPct}% (Gemini)
          </span>
          <span className="px-4 py-1 bg-red-200 rounded">
            Hard: {formData.hardPct}% (Claude)
          </span>
        </div>

        <div className="mt-10 flex space-x-4">
          <button
            onClick={() => {
              sessionStorage.setItem("comingBack", "true");
              navigate(-1);
            }}
            className="px-8 py-2 bg-primary text-white font-semibold rounded"
          >
            Go Back
          </button>
          <button
            onClick={handleGenerateQuestions}
            className="px-8 py-2 bg-primary text-white font-semibold rounded"
          >
            Generate Questions
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-40">
          <div className="bg-secondary border border-accent/40 p-6 rounded-lg shadow-lg max-w-sm w-full text-primary">
            <h3 className="text-xl mb-4">Do you want to proceed to generate questions?</h3>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleModalCancel}
                className="py-2 px-4 rounded border border-accent/60 text-primary bg-secondary hover:bg-accent/10"
              >
                Cancel
              </button>
              <button
                onClick={handleModalConfirm}
                className="py-2 px-4 rounded bg-primary text-white hover:opacity-90"
              >
                Yes, generate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Modal */}
      {isLoading && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50">
          <div className="bg-secondary text-primary border border-accent/40 p-6 rounded-lg shadow-lg flex flex-col items-center max-w-sm w-full">
            <svg
              className="animate-spin h-8 w-8 text-primary mb-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <p className="text-center text-lg">Generating questions, please wait...</p>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {error && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50">
          <div className="bg-secondary border border-accent/40 p-6 rounded-lg shadow-lg max-w-sm w-full text-primary">
            <h3 className="text-xl mb-4 text-red-600">Error Generating Questions</h3>
            <p className="mb-4">{error}</p>
            <div className="flex justify-end">
              <button
                onClick={() => setError(null)}
                className="py-2 px-4 rounded bg-primary text-white hover:opacity-90"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SummaryPage;
